import React, { useMemo, useState, useCallback, useEffect } from 'react';
import Button from '../../../shared/components/ui/Button';
import FloorCollapsibleSection from '../ui/FloorCollapsibleSection';
import { sortLevelsByName } from '../../utils/floorGrouping';

const filterLevels = (block) =>
  sortLevelsByName(
    block?.levels?.filter(
      (level) => level.floorType === 'RESIDENTIAL' || level.floorType === 'COMMERCIAL'
    ) || []
  );

const sortUnits = (units) =>
  [...(units || [])].sort((a, b) =>
    (a.unitName || '').localeCompare(b.unitName || '', undefined, {
      numeric: true,
      sensitivity: 'base'
    })
  );

/**
 * Modal to select units per block. Shows block tabs, search, select all/clear all,
 * and a grid of units by level. Used from Create PLC "Associated Units" section.
 */
export const PLCUnitSelectionModal = ({
  blocks,
  selectedUnitIdsByBlock = {},
  onConfirm,
  onClose
}) => {
  const [activeBlockId, setActiveBlockId] = useState(
    () => blocks?.[0]?.id ?? null
  );
  const [localSelected, setLocalSelected] = useState(() => ({
    ...selectedUnitIdsByBlock
  }));
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedFloors, setCollapsedFloors] = useState(() => new Set());

  const activeBlock = useMemo(
    () => blocks?.find((b) => b.id === activeBlockId) ?? null,
    [blocks, activeBlockId]
  );

  const levelsWithUnits = useMemo(() => {
    if (!activeBlock) return [];
    return filterLevels(activeBlock) || [];
  }, [activeBlock]);

  useEffect(() => {
    setCollapsedFloors(new Set());
  }, [activeBlockId]);

  const allUnitIdsInBlock = useMemo(() => {
    const ids = new Set();
    levelsWithUnits.forEach((level) => {
      (level.units || []).forEach((u) => ids.add(u.id));
    });
    return ids;
  }, [levelsWithUnits]);

  const selectedInBlock = useMemo(
    () => new Set(localSelected[activeBlockId] || []),
    [localSelected, activeBlockId]
  );

  const filteredLevels = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();
    if (!q) return levelsWithUnits;
    return levelsWithUnits
      .map((level) => ({
        ...level,
        units: (level.units || []).filter(
          (u) =>
            (u.unitName || '').toLowerCase().includes(q) ||
            String(u.id).includes(q)
        )
      }))
      .filter((level) => (level.units || []).length > 0);
  }, [levelsWithUnits, searchQuery]);

  const totalSelected = useMemo(() => {
    return Object.values(localSelected).reduce(
      (sum, ids) => sum + (ids?.length || 0),
      0
    );
  }, [localSelected]);

  const toggleFloor = useCallback((levelId) => {
    setCollapsedFloors((prev) => {
      const next = new Set(prev);
      if (next.has(levelId)) next.delete(levelId);
      else next.add(levelId);
      return next;
    });
  }, []);

  const handleToggleUnit = useCallback(
    (unitId) => {
      setLocalSelected((prev) => {
        const blockIds = prev[activeBlockId] || [];
        const set = new Set(blockIds);
        if (set.has(unitId)) set.delete(unitId);
        else set.add(unitId);
        return {
          ...prev,
          [activeBlockId]: Array.from(set)
        };
      });
    },
    [activeBlockId]
  );

  const handleSelectAll = useCallback(() => {
    setLocalSelected((prev) => ({
      ...prev,
      [activeBlockId]: Array.from(allUnitIdsInBlock)
    }));
  }, [activeBlockId, allUnitIdsInBlock]);

  const handleClearAll = useCallback(() => {
    setLocalSelected((prev) => ({
      ...prev,
      [activeBlockId]: []
    }));
  }, [activeBlockId]);

  const handleProceed = useCallback(() => {
    onConfirm(localSelected);
    onClose();
  }, [localSelected, onConfirm, onClose]);

  if (!blocks?.length) return null;

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex gap-2 mb-4 flex-shrink-0">
        {blocks.map((block) => (
          <button
            key={block.id}
            type="button"
            onClick={() => setActiveBlockId(block.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeBlockId === block.id
                ? 'bg-customBlue text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}>
            {block.name}
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3 flex-shrink-0">
        <span className="text-sm text-gray-600">
          {selectedInBlock.size} Units Selected
        </span>
        <input
          type="text"
          placeholder="Search units.."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1 min-w-[12rem] p-2 border rounded text-sm"
        />
        <div className="flex gap-2">
          <Button
            type="button"
            displayType="secondary"
            onClick={handleSelectAll}
            className="!py-1.5 !px-3 text-sm">
            Select All
          </Button>
          <button
            type="button"
            onClick={handleClearAll}
            className="py-1.5 px-3 text-sm rounded-lg bg-customBlue text-white border border-customBlue hover:opacity-90">
            Clear All
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-2">
        {filteredLevels.map((level) => {
          const units = sortUnits(level.units || []);
          const isOpen = !collapsedFloors.has(level.id);
          return (
            <FloorCollapsibleSection
              key={level.id}
              title={level.name}
              count={units.length}
              isOpen={isOpen}
              onToggle={() => toggleFloor(level.id)}
              bodyClassName="mt-2 flex flex-wrap gap-2 px-1 pb-1">
              {units.map((unit) => {
                const isSelected = selectedInBlock.has(unit.id);
                return (
                  <button
                    key={unit.id}
                    type="button"
                    onClick={() => handleToggleUnit(unit.id)}
                    className={`min-w-[3rem] px-3 py-2 rounded border text-sm font-medium transition-colors ${
                      isSelected
                        ? 'bg-customBlue text-white border-customBlue'
                        : 'bg-gray-100 text-gray-800 border-gray-300 hover:border-customBlue hover:bg-gray-50'
                    }`}>
                    {unit.unitName || unit.name || `Unit ${unit.id}`}
                  </button>
                );
              })}
            </FloorCollapsibleSection>
          );
        })}
      </div>

      <div className="mt-4 pt-4 border-t flex justify-between items-center flex-shrink-0">
        <span className="text-sm text-gray-500">
          {totalSelected} Units Selected — Click units to select or deselect
        </span>
        <div className="flex gap-2">
          <Button type="button" displayType="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={handleProceed}>
            Proceed ({totalSelected})
          </Button>
        </div>
      </div>
    </div>
  );
};
